import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ThemeService } from '../services/theme';

/**
 * Topbar accent picker.
 *
 * Opens a modal, previews the accent LIVE as you click through the swatches (so you judge it
 * against the real UI, not a chip), and only writes it to storage on Apply. Cancel puts back
 * whatever was saved. Once applied it persists across sessions until changed or reset.
 */
@Component({
  selector: 'app-theme-picker',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule],
  template: `
    <button
      type="button"
      class="tp-btn"
      (click)="openDialog()"
      aria-haspopup="dialog"
      title="Change theme colour">
      <span class="tp-dot" [style.background]="theme.current().swatch"></span>
      <i class="pi pi-palette"></i>
    </button>

    <p-dialog
      header="Theme colour"
      [visible]="open()"
      (visibleChange)="onVisible($event)"
      [modal]="true"
      [draggable]="true"
      [closable]="true"
      [dismissableMask]="true"
      [style]="{ width: '540px' }"
      styleClass="tp-dialog"
      appendTo="body">
      <p class="tp-lede">
        Pick an accent — the whole app updates instantly so you can see it in place.
        It stays applied until you change or reset it.
      </p>

      <div class="tp-grid">
        @for (t of theme.themes; track t.key; let i = $index) {
          <button
            type="button"
            class="tp-card"
            [class.active]="t.key === selected()"
            [style.--sw]="t.swatch"
            [style.animation-delay]="i * 22 + 'ms'"
            (click)="choose(t.key)"
            [attr.aria-pressed]="t.key === selected()">
            <span class="tp-chip" [style.background]="t.swatch">
              @if (t.key === selected()) { <i class="pi pi-check"></i> }
            </span>
            <span class="tp-name">{{ t.name }}</span>
          </button>
        }
      </div>

      <ng-template pTemplate="footer">
        <p-button
          label="Reset to default"
          icon="pi pi-refresh"
          [text]="true"
          severity="secondary"
          (onClick)="resetDefault()" />
        <span class="tp-spacer"></span>
        <p-button label="Cancel" [text]="true" severity="secondary" (onClick)="cancel()" />
        <p-button label="Apply" icon="pi pi-check" (onClick)="apply()" />
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }

      .tp-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 38px;
        padding: 0 10px;
        border-radius: 10px;
        border: 1px solid var(--line, #e7ecef);
        background: #fff;
        cursor: pointer;
        color: var(--muted, #6b7280);
        transition: box-shadow 0.18s var(--ease-out), border-color 0.18s var(--ease-out);
      }
      .tp-btn:hover {
        border-color: var(--brand, #007cc2);
        box-shadow: var(--ring);
      }

      .tp-dot {
        width: 14px;
        height: 14px;
        border-radius: 50%;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
      }

      .tp-lede {
        margin: 0 0 14px;
        font-size: 13px;
        color: var(--muted, #6b7280);
        line-height: 1.5;
      }

      .tp-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
        gap: 10px;
      }

      /* Cards fade up in sequence — enough to feel considered, short enough not to delay. */
      .tp-card {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 12px;
        border: 1px solid var(--line, #e7ecef);
        background: #fff;
        cursor: pointer;
        font: inherit;
        text-align: left;
        color: var(--text, #1f2937);
        transition: border-color 0.16s var(--ease-out), box-shadow 0.16s var(--ease-out),
          transform 0.16s var(--ease-out);
        animation: tp-in 0.26s var(--ease-out) both;
      }

      @keyframes tp-in {
        from {
          opacity: 0;
          transform: translateY(6px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .tp-card {
          animation: none;
        }
      }

      .tp-card:hover {
        transform: translateY(-1px);
        border-color: var(--sw);
        box-shadow: 0 8px 18px -10px var(--sw);
      }

      .tp-card.active {
        border-color: var(--sw);
        box-shadow: 0 0 0 2px var(--sw) inset;
      }

      .tp-chip {
        width: 26px;
        height: 26px;
        flex: 0 0 auto;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 12px;
        box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
      }

      .tp-name {
        font-size: 13px;
        font-weight: 600;
      }

      .tp-spacer {
        flex: 1 1 auto;
      }

      :host ::ng-deep .tp-dialog .p-dialog-footer {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    `,
  ],
})
export class ThemePicker {
  readonly theme = inject(ThemeService);
  readonly open = signal(false);

  /** The accent being previewed in the dialog (not yet committed). */
  readonly selected = signal<string>(this.theme.current().key);

  openDialog() {
    this.selected.set(this.theme.current().key);
    this.open.set(true);
  }

  /** Live preview only — nothing is persisted until Apply. */
  choose(key: string) {
    this.selected.set(key);
    this.theme.preview(key);
  }

  apply() {
    this.theme.set(this.selected());
    this.open.set(false);
  }

  cancel() {
    this.theme.revert();
    this.open.set(false);
  }

  resetDefault() {
    this.theme.reset();
    this.selected.set(this.theme.current().key);
  }

  /** Closing via the X or the mask counts as cancelling, so a preview is never left applied. */
  onVisible(v: boolean) {
    if (!v && this.open()) this.cancel();
    this.open.set(v);
  }
}
